import React from 'react';

import { Stack } from '@mui/material';
import { DateTime } from 'luxon';

import { Nullable } from '@clearverify/types';

import { useGetDashboardReportAndRevenueQuery } from 'src/api';
import {
  useDashboardLastUploadRoles,
  useDashboardUsageReports,
  useDashboardWorkLetterUsage,
  useDemoDashboard,
} from 'src/hooks/dashboard';
import { useCurrentUserPeo } from 'src/hooks/peo';
import {
  getDemoDashboardReportData,
  getDemoDashboardUsageReports,
  getDemoDashboardWorkLetterUsage,
} from 'src/utils/dashboard/demoDashboardData';

import {
  DashboardReportAndRevenueBlock,
  DashboardUsageReportsBlock,
  DashboardWorkLetterUsageBlock,
} from './components';
import { MIN_DATE_WORK_LETTER_REPORT } from './components/constants';

interface DashboardReportsMetricsProps {
  employerIds: string[];
  createdAtCompany: Nullable<string>;
  loading?: boolean;
}

export const DashboardReportsMetrics: React.FC<DashboardReportsMetricsProps> = ({
  employerIds,
  createdAtCompany,
  loading,
}) => {
  // UTC: the server groups the reports by UTC month, see `src/constants/dashboard`.
  const now = React.useRef(DateTime.utc());
  const { isDemoDashboardEnabled, scale } = useDemoDashboard(employerIds);

  const [usageReportStartDate, setUsageReportStartDate] = React.useState<Nullable<DateTime>>(
    now.current.minus({ months: 6 }).startOf('month'),
  );
  const [usageReportEndDate, setUsageReportEndDate] = React.useState<Nullable<DateTime>>(
    now.current.endOf('month'),
  );

  const [workLetterUsageStartDate, setWorkLetterUsageStartDate] = React.useState<
    Nullable<DateTime>
  >(now.current.minus({ months: 6 }).startOf('month'));
  const [workLetterUsageEndDate, setWorkLetterUsageEndDate] = React.useState<Nullable<DateTime>>(
    now.current.endOf('month'),
  );

  const minDate = createdAtCompany ? DateTime.fromISO(createdAtCompany, { zone: 'utc' }) : null;
  const shouldSkipQueries = loading || isDemoDashboardEnabled;

  /** Work letters only exist since the feature release, so the range never goes further back. */
  const workLetterUsageMinDate = React.useMemo(
    () =>
      createdAtCompany
        ? DateTime.max(
            DateTime.fromISO(createdAtCompany, { zone: 'utc' }),
            MIN_DATE_WORK_LETTER_REPORT,
          )
        : MIN_DATE_WORK_LETTER_REPORT,
    [createdAtCompany],
  );

  const workLetterUsageFrom = React.useMemo(
    () =>
      (workLetterUsageStartDate
        ? DateTime.max(workLetterUsageStartDate, workLetterUsageMinDate)
        : workLetterUsageMinDate
      ).startOf('month'),
    [workLetterUsageMinDate, workLetterUsageStartDate],
  );

  const { data: { getDashboardReportAndRevenue } = {}, loading: loadingReportAndRevenue } =
    useGetDashboardReportAndRevenueQuery({
      variables: {
        employerIds,
      },
      skip: shouldSkipQueries,
    });

  const { peo } = useCurrentUserPeo();

  /**
   * MDM reports the last load for one org at a time, so the card follows the employer filter: a
   * single employer (always the case for an Employer admin) is asked for directly, an empty filter
   * falls back to the whole PEO, and a multi-employer selection has no single-org answer to show.
   */
  const { lastUploadRoles } = useDashboardLastUploadRoles({
    employerId: employerIds.length === 1 ? employerIds[0] : null,
    peoId: employerIds.length ? null : peo?.id,
    skip: shouldSkipQueries,
  });

  const { dashboardUsageReportsItems, isDashboardUsageReportsLoading } = useDashboardUsageReports({
    variables: {
      employerIds,
      from:
        usageReportStartDate?.toISO() || now.current.minus({ months: 6 }).startOf('month').toISO(),
      to: usageReportEndDate?.toISO() || now.current.endOf('month').toISO(),
    },
    skip: shouldSkipQueries,
  });

  const { dashboardWorkLetterUsageItems, isDashboardWorkLetterUsageLoading } =
    useDashboardWorkLetterUsage({
      variables: {
        employerIds,
        from: workLetterUsageFrom.toISO() ?? '',
        to: workLetterUsageEndDate?.toISO() || now.current.endOf('month').toISO(),
      },
      skip: shouldSkipQueries,
    });

  const demoReportData = React.useMemo(() => getDemoDashboardReportData(scale), [scale]);

  const demoUsageReportsItems = React.useMemo(() => {
    if (!usageReportStartDate || !usageReportEndDate) return [];

    return getDemoDashboardUsageReports({
      scale,
      from: usageReportStartDate,
      to: usageReportEndDate,
    });
  }, [scale, usageReportEndDate, usageReportStartDate]);

  const demoWorkLetterUsageItems = React.useMemo(() => {
    if (!workLetterUsageEndDate) return [];

    return getDemoDashboardWorkLetterUsage({
      scale,
      from: workLetterUsageFrom,
      to: workLetterUsageEndDate,
    });
  }, [scale, workLetterUsageEndDate, workLetterUsageFrom]);

  const reportData = isDemoDashboardEnabled
    ? demoReportData
    : getDashboardReportAndRevenue?.dashboardReportData;

  const usageReportsItems = isDemoDashboardEnabled
    ? demoUsageReportsItems
    : dashboardUsageReportsItems;

  const workLetterUsageItems = isDemoDashboardEnabled
    ? demoWorkLetterUsageItems
    : dashboardWorkLetterUsageItems;

  const isReportBlockLoading = isDemoDashboardEnabled ? false : loadingReportAndRevenue || loading;
  const isUsageReportsLoading = isDemoDashboardEnabled
    ? false
    : isDashboardUsageReportsLoading || loading;
  const isWorkLetterUsageLoading = isDemoDashboardEnabled
    ? false
    : isDashboardWorkLetterUsageLoading || loading;

  return (
    <Stack padding={2} spacing={3} width="100%">
      <DashboardReportAndRevenueBlock
        activeRecordAmount={reportData?.activeRecordAmount}
        inactiveRecordAmount={reportData?.inactiveRecordAmount}
        pendingDataDisputeAmount={reportData?.pendingDataDisputeAmount}
        resolvedDataDisputeAmount={reportData?.resolvedDataDisputeAmount}
        revenueSharePayedAmount={
          getDashboardReportAndRevenue?.dashboardRevenueShareData.revenueSharePayedAmount || 0
        }
        revenueShareOwedAmount={
          getDashboardReportAndRevenue?.dashboardRevenueShareData.revenueShareOwedAmount || 0
        }
        hideRevenueShare={isDemoDashboardEnabled}
        lastUploadRoles={lastUploadRoles}
        loading={isReportBlockLoading}
      />
      <DashboardUsageReportsBlock
        dashboardUsageReportsItems={usageReportsItems}
        minDate={minDate}
        maxDate={now.current}
        loading={isUsageReportsLoading}
        startDate={usageReportStartDate}
        endDate={usageReportEndDate}
        onStartDateChange={setUsageReportStartDate}
        onEndDateChange={setUsageReportEndDate}
      />
      <DashboardWorkLetterUsageBlock
        dashboardWorkLetterUsageItems={workLetterUsageItems}
        minDate={workLetterUsageMinDate}
        maxDate={now.current}
        loading={isWorkLetterUsageLoading}
        startDate={workLetterUsageFrom}
        endDate={workLetterUsageEndDate}
        onStartDateChange={setWorkLetterUsageStartDate}
        onEndDateChange={setWorkLetterUsageEndDate}
      />
    </Stack>
  );
};
